import {
  createContext,
  useContext,
  useReducer,
  useRef,
  useCallback,
  type ReactNode,
  type Dispatch,
} from "react";
import type { PDFPageProxy } from "pdfjs-dist";

import type { ANNOTATION_TYPES, PLACEMENT_TYPE, ANNOTATION_SUBTYPES } from "constants/annotations";
import type { PrepPdfDesignation as Designation } from "common/pdf/interaction/prep/pdf_designation_fragment.graphql";
import type { DocumentBundleMembershipRole } from "graphql_globals";
import { debounce } from "util/timeout";

export const PADDING = 10;
export const MIN_FONT_SIZE = 8;
export const MIN_ANNOTATION_HEIGHT = 10;
const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

export type ToolData = {
  id: string;
  type: ObjectValues<typeof ANNOTATION_TYPES>;
  placementType: ObjectValues<typeof PLACEMENT_TYPE>;
  subtype?: ObjectValues<typeof ANNOTATION_SUBTYPES>;
  userId?: string;
  signerRole?: {
    index: string;
    role: DocumentBundleMembershipRole;
  };
  primaryDesignationId?: string;
  contactInformation?: {
    email?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
  };
  sourceDesignation?: Designation;
};
type Page = {
  idx: number;
  top: number;
  bottom: number;
};
type PDFState = {
  currentPage: number;
  currentToolData?: ToolData | undefined;
  currentSignerNumber: number;
  currentToolLocked: boolean;
  editingTextField: boolean;
  currentZoom: number;
  cachedZoom: number;
  visiblePages: Page[];
  scrollContainerRef?: { current: HTMLDivElement | null };
  viewerWrapperRef?: { current: HTMLDivElement | null };
  pages: PDFPageProxy[];
};
export type PdfAction =
  | { type: "selectTool"; payload: ToolData | undefined }
  | { type: "resetTools" }
  | { type: "selectCurrentSigner"; payload: number }
  | { type: "lockTool"; payload: boolean }
  | { type: "updatePage"; payload: number }
  | { type: "editingTextField"; payload: boolean }
  | { type: "setCurrentZoom"; payload: number }
  | { type: "setCachedZoom"; payload: number }
  | { type: "setVisiblePages"; payload: Page[] }
  | { type: "setPages"; payload: PDFPageProxy[] }
  | {
      type: "setElementRefs";
      payload: {
        scrollContainerRef: { current: HTMLDivElement | null };
        viewerWrapperRef: { current: HTMLDivElement | null };
      } | null;
    };

const pdfReducer = (state: PDFState, action: PdfAction) => {
  switch (action.type) {
    case "selectTool": {
      return { ...state, currentToolData: action.payload };
    }
    case "resetTools": {
      return { ...state, currentToolData: undefined, currentSignerNumber: 1 };
    }
    case "updatePage": {
      if (action.payload === state.currentPage) {
        return state;
      }
      return { ...state, currentPage: action.payload };
    }
    case "selectCurrentSigner": {
      return { ...state, currentSignerNumber: action.payload };
    }
    case "lockTool": {
      return { ...state, currentToolLocked: action.payload };
    }
    case "editingTextField": {
      return { ...state, editingTextField: action.payload };
    }
    case "setCurrentZoom": {
      const zoom =
        action.payload > MAX_SCALE
          ? MAX_SCALE
          : action.payload < MIN_SCALE
            ? MIN_SCALE
            : action.payload;
      return {
        ...state,
        currentZoom: zoom,
      };
    }
    case "setCachedZoom": {
      const zoom =
        action.payload > MAX_SCALE
          ? MAX_SCALE
          : action.payload < MIN_SCALE
            ? MIN_SCALE
            : action.payload;
      return {
        ...state,
        cachedZoom: zoom,
      };
    }
    case "setVisiblePages": {
      if (action.payload === state.visiblePages || !action.payload.length) {
        return state;
      }
      return { ...state, visiblePages: action.payload };
    }
    case "setPages": {
      return { ...state, pages: action.payload };
    }
    case "setElementRefs": {
      if (!action.payload) {
        return {
          ...state,
          scrollContainerRef: undefined,
          viewerWrapperRef: undefined,
        };
      }
      return {
        ...state,
        scrollContainerRef: action.payload.scrollContainerRef,
        viewerWrapperRef: action.payload.viewerWrapperRef,
      };
    }
    default:
      return state;
  }
};

const DEFAULT_STATE = {
  currentPage: 1,
  currentSignerNumber: 1,
  currentToolLocked: false,
  editingTextField: false,
  currentZoom: 1, // currentZoom is the most up to date scale, it is updated every time the zoom level changes
  cachedZoom: 1, // cachedZoom is used for things that are expensive to render i.e. the pdf page canvas, updates to it should always be debounced
  visiblePages: [],
  pages: [],
};

const PDFContext = createContext<PDFState>(DEFAULT_STATE);
const PDFDispatchContext = createContext<Dispatch<PdfAction> | null>(null);

export function usePdfContext() {
  return useContext(PDFContext);
}
export function usePDFDispatch() {
  return useContext(PDFDispatchContext);
}

export const PDFViewerContext = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(pdfReducer, DEFAULT_STATE);
  return (
    <PDFContext.Provider value={state}>
      <PDFDispatchContext.Provider value={dispatch}>{children}</PDFDispatchContext.Provider>
    </PDFContext.Provider>
  );
};

const DEFAULT_SCALE_DELTA = 1.1;

function MathClamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max);
}
const findPage = (
  { top: pageTop, bottom }: { idx: number; top: number; bottom: number },
  scrollTop: number,
) => {
  const adjustment = (bottom - pageTop) * 0.33;
  return pageTop <= scrollTop + adjustment && bottom >= scrollTop + adjustment;
};

function getVisiblePages({
  scrollEl,
  scrollTopOverride,
  viewEl,
  pages,
  scale,
}: {
  scrollEl: HTMLDivElement;
  scrollTopOverride?: number;
  viewEl: HTMLDivElement;
  pages: PDFPageProxy[];
  scale: number;
}) {
  const top = typeof scrollTopOverride === "number" ? scrollTopOverride : scrollEl.scrollTop;
  const { scrollHeight, scrollWidth } = viewEl;
  const viewport = { height: scrollHeight, width: scrollWidth };
  const pageViewports = pages.map((page) => page.getViewport({ scale }));
  const heights = pageViewports.map(({ height }) => height + PADDING);
  const range = top + viewport.height;
  const visible = heights.reduce<{ idx: number; top: number; bottom: number }[]>(
    (acc, height, idx) => {
      const previous = heights.slice(0, idx).reduce((acc, h) => acc + h, 0);
      const pageTop = previous;
      const pageBottom = previous + height;
      // page bottom is below the top of the view port and within the viewport
      // page top is within the viewport
      if ((pageBottom >= top && pageBottom <= range) || (pageTop <= range && pageTop >= top)) {
        acc.push({ idx, top: pageTop, bottom: pageBottom });
      }
      return acc;
    },
    [],
  );
  return visible;
}

export function setVisiblePages({
  scrollEl,
  scrollTopOverride,
  viewEl,
  pages,
  scale,
  dispatch,
}: {
  scrollEl: HTMLDivElement;
  scrollTopOverride?: number;
  viewEl: HTMLDivElement;
  pages: PDFPageProxy[];
  scale: number;
  dispatch: ReturnType<typeof usePDFDispatch>;
}) {
  const visible = getVisiblePages({
    scrollEl,
    scrollTopOverride,
    viewEl,
    pages,
    scale,
  });
  dispatch?.({ type: "setVisiblePages", payload: visible });
}

export function useAdjustZoomIncrementally() {
  // This hook returns a handler to adjust the current and cached zoom incrementally.
  // For example, this is useful for buttons where we want to update the zoom level per click.
  const { scrollContainerRef, currentZoom, viewerWrapperRef, pages } = usePdfContext();
  const dispatch = usePDFDispatch();

  const callback = useCallback(
    // Factor 1 incrementally increases the zoom level
    // Factor -1 incrementally decreases the zoom level
    // Factor 0 resets the zoom level to 1
    (factor: 1 | -1 | 0) => {
      if (!scrollContainerRef?.current || !viewerWrapperRef?.current) {
        return;
      }
      const visiblePagesProps = {
        scrollEl: scrollContainerRef.current,
        viewEl: viewerWrapperRef.current,
        pages,
        dispatch,
      };
      if (factor === 0) {
        dispatch?.({ type: "setCurrentZoom", payload: 1 });
        setVisiblePages({
          ...visiblePagesProps,
          scale: 1,
        });
        debounce(() => {
          dispatch?.({ type: "setCachedZoom", payload: 1 });
        }, 250)();
        return;
      }

      const adjustedZoom = 1.25 ** factor * currentZoom;
      setVisiblePages({
        ...visiblePagesProps,
        scale: adjustedZoom,
      });

      dispatch?.({ type: "setCurrentZoom", payload: adjustedZoom });
      debounce(() => {
        dispatch?.({ type: "setCachedZoom", payload: adjustedZoom });
      }, 250)();
    },
    [viewerWrapperRef?.current, scrollContainerRef?.current, currentZoom, pages],
  );
  return callback;
}

export function useScrollToPage() {
  const dispatch = usePDFDispatch();
  const { scrollContainerRef, currentZoom, viewerWrapperRef, pages } = usePdfContext();
  const callback = useCallback(
    (pageNumber: number) => {
      if (scrollContainerRef?.current && viewerWrapperRef?.current) {
        const pageViewports = pages.map((page) => page.getViewport({ scale: currentZoom }));
        const heights = pageViewports.map(({ height }) => height + PADDING);
        const scrollTop = heights.slice(0, pageNumber - 1).reduce((acc, h) => acc + h, 0);
        setVisiblePages({
          scrollEl: scrollContainerRef.current,
          scrollTopOverride: scrollTop,
          viewEl: viewerWrapperRef.current,
          pages,
          scale: currentZoom,
          dispatch,
        });
        scrollContainerRef.current.scrollTop = scrollTop;
        dispatch?.({ type: "updatePage", payload: pageNumber });
      }
    },
    [viewerWrapperRef?.current, scrollContainerRef?.current, currentZoom, pages],
  );
  return callback;
}

export const useGetZoomHandlers = (
  wrapper: { current: HTMLDivElement | null } | null,
  scrollContainer: { current: HTMLDivElement | null } | null,
  zoomRef: { current: number },
  currentPageRef: { current: number },
) => {
  // useGetZoomHandlers returns the onWheel event handler which sets the zoom level
  // of the pdf pages based on the user's wheel events.
  // TODO: this should also return the touch handlers for pinch to zoom
  const dispatch = usePDFDispatch();
  const debouncedDispatch = useCallback(
    debounce((action: PdfAction, cb?: () => void) => {
      dispatch?.(action);
      cb?.();
    }, 250),
    [],
  );

  const accumulator = useRef({
    _wheelUnusedFactor: 0,
    _wheelUnusedTicks: 0,
    accumulatedScale: zoomRef.current,
  });
  if (!wrapper?.current) {
    return { onWheel: undefined };
  }
  function accumulateTicks(ticks: number, key: keyof typeof accumulator.current) {
    if (
      (accumulator.current[key] > 0 && ticks < 0) ||
      (accumulator.current[key] < 0 && ticks > 0)
    ) {
      accumulator.current[key] = 0;
    }
    accumulator.current[key] += ticks;
    const wholeTicks = Math.trunc(accumulator.current[key]);
    accumulator.current[key] -= wholeTicks;
    return wholeTicks;
  }
  function accumulateFactor(
    previousScale: number,
    factor: number,
    key: keyof typeof accumulator.current,
  ) {
    if (factor === 1) {
      return 1;
    }
    if (
      (accumulator.current[key] > 1 && factor < 1) ||
      (accumulator.current[key] < 1 && factor > 1)
    ) {
      accumulator.current[key] = 1;
    }
    const newFactor =
      Math.floor(previousScale * factor * accumulator.current[key] * 100) / (100 * previousScale);
    accumulator.current[key] = factor / newFactor;
    return newFactor;
  }

  function scrollPageIntoView({
    destinationInfo,
    initPageHeight,
    pages,
  }: {
    initPageHeight: number;
    destinationInfo: {
      top: number | null;
      left: number | null;
      newScale: number;
      scaleDiff: number;
    };
    pages: PDFPageProxy[];
  }) {
    const currentScale = zoomRef.current;
    const { top: destTop, left: destLeft, newScale } = destinationInfo;

    const newX = destTop !== null ? destTop : 0;
    const newY = destLeft !== null ? destLeft : initPageHeight;
    if (scrollContainer?.current && wrapper?.current) {
      const top = scrollContainer.current.scrollTop;
      const left = scrollContainer.current.scrollLeft;
      scrollContainer.current.scrollLeft = ((left + newX) / currentScale) * newScale;
      scrollContainer.current.scrollTop = ((top + newY) / currentScale) * newScale;

      const visible = getVisiblePages({
        scrollEl: scrollContainer.current,
        viewEl: wrapper.current,
        pages,
        scale: newScale,
      });
      dispatch?.({ type: "setVisiblePages", payload: visible });
      const newPage = visible.find((vis) => findPage(vis, top));
      if (newPage) {
        dispatch?.({ type: "updatePage", payload: newPage.idx + 1 });
      }
    }
  }
  function setScale(value: number, initPageHeight: number, pages: PDFPageProxy[]) {
    if (value > 0) {
      accumulator.current.accumulatedScale = value;
      if (scrollContainer?.current) {
        dispatch?.({ type: "setCurrentZoom", payload: value });
        const top = scrollContainer.current.scrollTop;
        const left = scrollContainer.current.scrollLeft;
        const scaleDiff = value / zoomRef.current - 1;
        const destinationInfo = { left, top, newScale: value, scaleDiff };
        scrollPageIntoView({ destinationInfo, initPageHeight, pages });
        debouncedDispatch({
          type: "setCachedZoom",
          payload: value,
        });
      }
    }
  }

  function updateScale({
    scaleFactor = null,
    steps = null,
    initPageHeight,
    pages,
  }: {
    scaleFactor: number | null;
    steps: number | null;
    initPageHeight: number;
    pages: PDFPageProxy[];
  }) {
    if (steps === null && scaleFactor === null) {
      throw new Error(
        "Invalid updateScale options: either `steps` or `scaleFactor` must be provided.",
      );
    }
    let newScale = accumulator.current.accumulatedScale;
    newScale = Math.round(newScale * (scaleFactor || 1) * 100) / 100;
    if (scaleFactor && scaleFactor > 0 && scaleFactor !== 1) {
      newScale = Math.round(newScale * scaleFactor * 100) / 100;
    } else if (steps) {
      const delta = steps > 0 ? DEFAULT_SCALE_DELTA : 1 / DEFAULT_SCALE_DELTA;
      const round = steps > 0 ? Math.ceil : Math.floor;
      steps = Math.abs(steps);
      do {
        const rounded = parseFloat((newScale * delta).toFixed(2));
        newScale = round(rounded * 10) / 10;
      } while (--steps > 0);
    }
    newScale = MathClamp(newScale, MIN_SCALE, MAX_SCALE);
    setScale(newScale, initPageHeight, pages);
  }

  function normalizeWheelEventDirection(evt: WheelEvent) {
    let delta = Math.hypot(evt.deltaX, evt.deltaY);
    const angle = Math.atan2(evt.deltaY, evt.deltaX);
    if (-0.25 * Math.PI < angle && angle < 0.75 * Math.PI) {
      delta = -delta;
    }
    return delta;
  }

  function onWheel(evt: WheelEvent, pageHeights: number[], pages: PDFPageProxy[]) {
    const deltaMode = evt.deltaMode;
    let scaleFactor = Math.exp(-evt.deltaY / 100);
    const currentScale = zoomRef.current;
    const isPinchToZoom =
      evt.ctrlKey &&
      deltaMode === WheelEvent.DOM_DELTA_PIXEL &&
      evt.deltaX === 0 &&
      Math.abs(scaleFactor - 1) < 0.05 &&
      evt.deltaZ === 0;
    if (isPinchToZoom || evt.ctrlKey || evt.metaKey) {
      evt.preventDefault();
      const pageHeightsWithIdx = pageHeights.map((initHeight, idx) => {
        return {
          initHeight,
          pageIdx: idx,
        };
      });
      const { initHeight } =
        pageHeightsWithIdx.find(({ initHeight }) => {
          if (scrollContainer?.current) {
            const scaledHeight = initHeight * currentScale;
            return scrollContainer.current.scrollTop <= scaledHeight;
          }
          return null;
        }) || pageHeightsWithIdx[currentPageRef.current - 1];

      if (isPinchToZoom) {
        scaleFactor = accumulateFactor(currentScale, scaleFactor, "_wheelUnusedFactor");
        updateScale({
          steps: null,
          scaleFactor,
          initPageHeight: initHeight,
          pages,
        });
      } else {
        const delta = normalizeWheelEventDirection(evt);
        let ticks = 0;
        if (deltaMode === WheelEvent.DOM_DELTA_LINE || deltaMode === WheelEvent.DOM_DELTA_PAGE) {
          ticks =
            Math.abs(delta) >= 1 ? Math.sign(delta) : accumulateTicks(delta, "_wheelUnusedTicks");
        } else {
          const PIXELS_PER_LINE_SCALE = 30;
          ticks = accumulateTicks(delta / PIXELS_PER_LINE_SCALE, "_wheelUnusedTicks");
        }
        updateScale({
          steps: ticks,
          scaleFactor: null,
          initPageHeight: initHeight,
          pages,
        });
      }
    } else if (scrollContainer?.current && wrapper?.current) {
      const top = scrollContainer.current.scrollTop;
      const visible = getVisiblePages({
        scrollEl: scrollContainer.current,
        viewEl: wrapper.current,
        pages,
        scale: currentScale,
      });
      const lastPage = visible[visible.length - 1];
      const newPage =
        scrollContainer.current.scrollHeight ===
        scrollContainer.current.scrollTop + wrapper.current.scrollHeight
          ? lastPage
          : visible.find((vis) => findPage(vis, top));
      dispatch?.({ type: "setVisiblePages", payload: visible });
      if (newPage) {
        dispatch?.({ type: "updatePage", payload: newPage.idx + 1 });
      }
    }
  }

  return { onWheel };
};
